import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

// Layout Components
import Header from './components/Header';
import Footer from './components/Footer';
import JinglePlayer from './components/JinglePlayer';

// Pages
import HomePage from './pages/HomePage';
import HotTubsPage from './pages/HotTubsPage';
import SwimSpasPage from './pages/SwimSpasPage';
import SaunasPage from './pages/SaunasPage';
import ColdPlungesPage from './pages/ColdPlungesPage';
import LeisureCraftPage from './pages/LeisureCraftPage';
import WellnessPage from './pages/WellnessPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import EventsPage from './pages/EventsPage';
import FinancingPage from './pages/FinancingPage';
import SpaButlerPage from './pages/SpaButlerPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CoversPage from './pages/CoversPage';
import ChemicalsPage from './pages/ChemicalsPage';
import AnatomyPage from './pages/AnatomyPage';
import BalneotherapyPage from './pages/BalneotherapyPage';
import JetsPage from './pages/JetsPage';
import ARVisualizerPage from './pages/ARVisualizerPage';
import BrochurePage from './pages/BrochurePage';
import MembershipPage from './pages/MembershipPage';
import NaturalSpasPage from './pages/NaturalSpasPage';
import PDCSpasPage from './pages/PDCSpasPage';
import ClearwaterSpasPage from './pages/ClearwaterSpasPage';
import FaqPage from './pages/FaqPage';
import AreasPage from './pages/AreasPage';
import AreaPage from './pages/AreaPage';
import BuyingGuidePage from './pages/BuyingGuidePage';
import PDCProductPage from './pages/PDCProductPage';



import './App.css';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  // Pause any audible (non-muted) media on route change so sound never bleeds across pages.
  useEffect(() => {
    window.scrollTo(0, 0);
    document.querySelectorAll('video, audio').forEach((m) => {
      try { if (!m.muted) m.pause(); } catch (e) { /* noop */ }
    });
  }, [pathname]);

  // Also pause the currently-playing video the instant the user clicks a link or hits back,
  // while the element is still mounted (browsers keep detached media playing otherwise).
  useEffect(() => {
    const stopAudible = () => {
      document.querySelectorAll('video, audio').forEach((m) => {
        try { if (!m.paused && !m.muted) m.pause(); } catch (e) { /* noop */ }
      });
    };
    const onClick = (e) => {
      if (e.target && e.target.closest && e.target.closest('a[href]')) stopAudible();
    };
    document.addEventListener('click', onClick, true);
    window.addEventListener('popstate', stopAudible);
    return () => {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('popstate', stopAudible);
    };
  }, []);

  return null;
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <div className="App">
          <a href="#main-content" className="skip-link">Skip to main content</a>
          <Header />
          <main id="main-content" role="main">
            <Routes>
              {/* Main Pages */}
              <Route path="/" element={<HomePage />} />
              
              {/* Shop Pages - Individual Brand Pages */}
              <Route path="/hot-tubs" element={<HotTubsPage />} />
              <Route path="/grand-river-spas" element={<Navigate to="/hot-tubs" replace />} />
              <Route path="/viking-spas" element={<Navigate to="/hot-tubs" replace />} />
              <Route path="/dynasty-spas" element={<Navigate to="/hot-tubs" replace />} />
              <Route path="/swim-spas" element={<SwimSpasPage />} />
              <Route path="/saunas" element={<SaunasPage />} />
              <Route path="/cold-plunges" element={<ColdPlungesPage />} />
              <Route path="/leisurecraft" element={<LeisureCraftPage />} />
              
              {/* Product Detail */}
              <Route path="/products/:id" element={<ProductDetailPage />} />
              
              {/* Wellness */}
              <Route path="/wellness" element={<WellnessPage />} />
              
              {/* Discover Pages */}
              <Route path="/about" element={<AboutPage />} />
              <Route path="/covers" element={<CoversPage />} />
              <Route path="/chemicals" element={<ChemicalsPage />} />
              <Route path="/anatomy-of-a-spa" element={<AnatomyPage />} />
              <Route path="/balneotherapy" element={<BalneotherapyPage />} />
              <Route path="/jets" element={<JetsPage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/financing" element={<FinancingPage />} />
              <Route path="/spa-butler" element={<SpaButlerPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/areas" element={<AreasPage />} />
              <Route path="/areas/:slug" element={<AreaPage />} />
              <Route path="/hot-tub-buying-guide" element={<BuyingGuidePage />} />
              
              {/* Contact */}
              <Route path="/contact" element={<ContactPage />} />
              
              {/* AR Visualizer */}
              <Route path="/ar-visualizer" element={<ARVisualizerPage />} />
              
              {/* Brochures */}
              <Route path="/brochures" element={<BrochurePage />} />
              
              {/* Membership */}
              <Route path="/membership" element={<MembershipPage />} />

              {/* Natural Rock Spas (dealer) */}
              <Route path="/natural-rock-spas" element={<NaturalSpasPage />} />

              {/* PDC Spas (dealer) */}
              <Route path="/pdc-spas" element={<PDCSpasPage />} />
              <Route path="/clearwater-spas" element={<ClearwaterSpasPage />} />
              <Route path="/pdc-spas/:productId" element={<PDCProductPage />} />
              
              {/* Aquora / Dynasty pages removed; redirect stragglers to cold plunges */}
              <Route path="/aquora-structure" element={<Navigate to="/cold-plunges" replace />} />
              <Route path="/aquora-warranty" element={<Navigate to="/cold-plunges" replace />} />
              <Route path="/aquora-controls" element={<Navigate to="/cold-plunges" replace />} />
              <Route path="/aquora-ozone" element={<Navigate to="/cold-plunges" replace />} />
              <Route path="/aquora-engineering" element={<Navigate to="/cold-plunges" replace />} />
              <Route path="/aquora-skirting" element={<Navigate to="/cold-plunges" replace />} />
              <Route path="/aquora-installation" element={<Navigate to="/cold-plunges" replace />} />
              <Route path="/aquora-brochure" element={<Navigate to="/cold-plunges" replace />} />
            </Routes>
          </main>
          <Footer />
          <JinglePlayer />
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
